/* global CVP */

// ### Store
//
// Options for saving to localStorage or sessionStorage.

'use strict';

var _ = require('./vendor/underscore');
var _logger = require('./logger');


var localStorageImpl = {

	_testKey : '__testlocalstorage__',
	_ready : undefined,

	_hasLocalStorage : function () {
		try {
			return 'localStorage' in window && window.localStorage !== null;
		} catch (e) {
			return false;
		}
	},

	_testLocalStorage : function () {
		try {
			this._setItem(this._testKey, this._testKey);
		} catch (e) {
			return false;
		}
		if (this._getItem(this._testKey) !== this._testKey) {
			return false;
		}
		this._removeItem(this._testKey);
		return true;
	},

	isSupported : function () {
		if (this._ready === undefined) {
			this._ready = this._hasLocalStorage() && this._testLocalStorage();
		}
		return this._ready;
	},

	_wrap : function (fn, args) {
		if (this.isSupported()) {
			return fn.apply(this, args);
		}
		else {
			_logger.warn('Unable to access localStorage.');
		}
	},

	// throws exception when using Private mode in iOS Safari
	_setItem : function (key, value) {
		return window.localStorage.setItem(key, value);
	},
	_getItem : function (key) {
		return window.localStorage.getItem(key);
	},
	_removeItem : function (key) {
		return window.localStorage.removeItem(key);
	},

	setItem : function (/*key, value*/) {
		return this._wrap(this._setItem, arguments);
	},
	getItem : function (/*key*/) {
		return this._wrap(this._getItem, arguments);
	},
	removeItem : function (/*key*/) {
		return this._wrap(this._removeItem, arguments);
	}

};

var sessionStorageImpl = {

	_testKey : '__testsessionstorage__',
	_ready : undefined,

	_hasSessionStorage : function () {
		try {
			return 'sessionStorage' in window && window.sessionStorage !== null;
		} catch (e) {
			return false;
		}
	},

	_testSessionStorage : function () {
		try {
			this._setItem(this._testKey, this._testKey);
		} catch (e) {
			return false;
		}
		if (this._getItem(this._testKey) !== this._testKey) {
			return false;
		}
		this._removeItem(this._testKey);
		return true;
	},

	isSupported : function () {
		if (this._ready === undefined) {
			this._ready = this._hasSessionStorage() && this._testSessionStorage();
		}
		return this._ready;
	},

	_wrap : function (fn, args) {
		if (this.isSupported()) {
			return fn.apply(this, args);
		}
		else {
			_logger.warn('Unable to access sessionStorage.');
		}
	},

	// throws exception when using Private mode in iOS Safari
	_setItem : function (key, value) {
		return window.sessionStorage.setItem(key, value);
	},
	_getItem : function (key) {
		return window.sessionStorage.getItem(key);
	},
	_removeItem : function (key) {
		return window.sessionStorage.removeItem(key);
	},

	setItem : function (/*key, value*/) {
		return this._wrap(this._setItem, arguments);
	},
	getItem : function (/*key*/) {
		return this._wrap(this._getItem, arguments);
	},
	removeItem : function (/*key*/) {
		return this._wrap(this._removeItem, arguments);
	}

};

var StoreData = (function () {

	function StoreData(value, options) {
		this.value = value;
		this.timeStamp = options && options.timeStamp || _.now();
		if (options && options.expires) {
			this.expires = options.expires;
		}
	}

	StoreData.parse = function (storedString) {
		var obj;
		var value;
		var options;

		try {
			obj = CVP.JSON.parse(storedString);
			if ('data' in obj) {
				value = obj.data;
			}
			if ('meta' in obj) {
				options = obj.meta;
			}
		}
		catch (e) {
			return null;
		}

		return new StoreData(value, options);
	};

	StoreData.prototype.toObject = function () {
		var obj = {};

		obj.data = this.value;
		obj.meta = {
			timeStamp: this.timeStamp
		};

		if (this.expires) {
			obj.meta.expires = this.expires;
		}

		return obj;
	};

	StoreData.prototype.stringify = function () {
		var string;

		try {
			string = CVP.JSON.stringify(this.toObject());
		}
		catch (e) {
			string = null;
		}

		return string;
	};

	StoreData.prototype.isExpired = function () {
		return this.expires && _.now() > this.expires;
	};

	return StoreData;

}());

function setItem(key, value, options) {
	/* jshint validthis:true */

	this.removeItem(key);

	if (value === null || value === undefined) {
		_logger.log('[Store:setItem] No value to save for key "' + key + '"');
		return true;
	}

	var data = new StoreData(value, options);

	if (data.isExpired()) {
		_logger.log('[Store:setItem] Item of key "' + key + '" has expired and will not be stored.');
		return true;
	}

	var string = data.stringify();

	if (string === null) {
		_logger.debug('[Store:setItem] Item of key "' + key + '" could not be stringified and will not be stored.');
		return false;
	}

	try {
		this.setItem(key, string);
		return true;
	}
	catch (e) {
		_logger.warn('[Store:setItem] Item of key "' + key + '" could not be stored: ', e);
		return false;
	}
}

function getItem(key) {
	/* jshint validthis:true */

	var string;

	try {
		string = this.getItem(key);
	}
	catch (e) {
		_logger.warn('[Store:getItem] Item of key "' + key + '" could not be retrieved: ', e);
		return false;
	}

	if (string === null) {
		_logger.log('[Store:getItem] Item of key "' + key + '" did not return a value.');
		return null;
	}

	var data = StoreData.parse(string);

	if (!data) {
		_logger.debug('[Store:getItem] Item of key "' + key + '" could not be parsed and will not be retrieved.');
		return false;
	}

	if (data.isExpired()) {
		_logger.log('[Store:getItem] Item of key "' + key + '" has expired and will not be retrieved.');
		this.removeItem(key);
		return null;
	}

	return data.value;
}

function removeItem(key) {
	/* jshint validthis:true */

	try {
		this.removeItem(key);
		return true;
	}
	catch (e) {
		_logger.warn('[Store:removeItem] Item of key "' + key + '" could not be removed: ', e);
		return false;
	}
}


exports.set = _.bind(setItem, localStorageImpl);
exports.get = _.bind(getItem, localStorageImpl);
exports.remove = _.bind(removeItem, localStorageImpl);
exports.supportsLocalStorage = _.bind(localStorageImpl.isSupported, localStorageImpl);

exports.setSession = _.bind(setItem, sessionStorageImpl);
exports.getSession = _.bind(getItem, sessionStorageImpl);
exports.removeSession = _.bind(removeItem, sessionStorageImpl);
exports.supportsSessionStorage = _.bind(sessionStorageImpl.isSupported, sessionStorageImpl);
